import { useEffect, useState } from 'react'
import { listParameters, listRuleSets } from '../../../api/cartridgeStudio'
import type { AiDraftKind } from '../../../types/aiCartridgeDraft'
import type { ParameterView, RuleSetView } from '../../../types/cartridgeStudio'

export interface AiDraftDiffViewProps {
  programId: string
  draftKind: AiDraftKind
  content: unknown
}

interface DiffField {
  label: string
  current: string
  proposed: string
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function fmt(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * A field-level diff of the proposed `content` against the target's current canon/draft state, for
 * the two draft kinds this Studio can currently read a comparable "current" state for.
 *
 * <b>Disclosed gap, not invented.</b> `EligibilityModel` has no `GET .../eligibility-models?program_id=`
 * (or similar) list endpoint anywhere in `module.eligibility` — `EligibilityTab` itself only tracks
 * "the current model" via a per-browser local cache, not a server list (confirmed by reading
 * `EligibilityTab.tsx`). So an `eligibility_model` draft renders its proposed decision-table content
 * only, honestly labelled as such, rather than a fabricated "no changes" comparison — inventing that
 * list endpoint is out of this UI-only story's scope (no backend surface invented, per the design doc).
 */
export function AiDraftDiffView({ programId, draftKind, content }: AiDraftDiffViewProps) {
  const [ruleSets, setRuleSets] = useState<RuleSetView[] | null>(null)
  const [parameters, setParameters] = useState<ParameterView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const contentRecord = asRecord(content)

  useEffect(() => {
    const controller = new AbortController()
    setLoadError(null)
    if (draftKind === 'rule_set') {
      listRuleSets(programId, controller.signal)
        .then((page) => {
          if (!controller.signal.aborted) setRuleSets(page.items)
        })
        .catch(() => {
          if (!controller.signal.aborted) setLoadError('Could not load the current RuleSet state for comparison.')
        })
    } else if (draftKind === 'parameter') {
      const ruleSetId = typeof contentRecord.ruleSetId === 'string' ? contentRecord.ruleSetId : null
      if (!ruleSetId) {
        setParameters([])
        return () => controller.abort()
      }
      listParameters(ruleSetId, controller.signal)
        .then((page) => {
          if (!controller.signal.aborted) setParameters(page.items)
        })
        .catch(() => {
          if (!controller.signal.aborted) setLoadError('Could not load the current Parameter state for comparison.')
        })
    }
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, draftKind, JSON.stringify(content)])

  if (draftKind === 'eligibility_model') {
    const decision = asRecord(contentRecord.decision)
    const rules = Array.isArray(decision.rules) ? decision.rules : []
    return (
      <div role="group" aria-label="Proposed decision table" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
        <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          No comparable "current EligibilityModel" read exists in this Studio yet — showing the proposed
          decision table only.
        </p>
        <div style={{ fontSize: 'var(--iw-size-xs)' }}>
          <div>Proposed model version: <strong>{fmt(contentRecord.version)}</strong></div>
          <div>Decision key: <strong>{fmt(decision.key)}</strong> (kind={fmt(decision.kind)}, hitPolicy={fmt(decision.hitPolicy)})</div>
          <div>{rules.length} drafted rule{rules.length === 1 ? '' : 's'}:</div>
          <ul>
            {rules.map((rule, i) => {
              const r = asRecord(rule)
              return (
                <li key={i}>
                  order {fmt(r.order)}: conditions {fmt(r.conditions)} &rarr; conclusions {fmt(r.conclusions)}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    )
  }

  if (loadError) {
    return <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-danger)' }}>{loadError}</p>
  }

  let fields: DiffField[] = []
  if (draftKind === 'rule_set') {
    if (ruleSets === null) {
      return <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Loading current RuleSet state…</p>
    }
    const current = ruleSets.reduce<RuleSetView | null>(
      (max, rs) => (max === null || rs.structureVersion > max.structureVersion ? rs : max),
      null,
    )
    fields = [
      { label: 'Structure version', current: fmt(current?.structureVersion ?? null), proposed: fmt(contentRecord.structureVersion) },
      { label: 'Valid from', current: fmt(current?.validFrom ?? null), proposed: fmt(contentRecord.validFrom) },
      { label: 'Valid to', current: fmt(current?.validTo ?? null), proposed: fmt(contentRecord.validTo) },
    ]
  } else if (draftKind === 'parameter') {
    if (parameters === null) {
      return <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Loading current Parameter state…</p>
    }
    const current = parameters.find((p) => p.key === contentRecord.key) ?? null
    fields = [
      { label: 'Key', current: fmt(current?.key ?? null), proposed: fmt(contentRecord.key) },
      { label: 'Value', current: fmt(current?.value ?? null), proposed: fmt(contentRecord.value) },
      { label: 'Valid from', current: fmt(current?.validFrom ?? null), proposed: fmt(contentRecord.validFrom) },
      { label: 'Valid to', current: fmt(current?.validTo ?? null), proposed: fmt(contentRecord.validTo) },
    ]
  }

  return (
    <div role="group" aria-label="Proposed vs current" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
      {fields.map((f) => {
        const changed = f.current !== f.proposed
        return (
          <div key={f.label} style={{ display: 'flex', gap: 'var(--iw-space-2)', fontSize: 'var(--iw-size-xs)' }}>
            <span style={{ minWidth: 110, color: 'var(--iw-text-muted)' }}>{f.label}</span>
            {changed ? (
              <>
                <del style={{ color: 'var(--iw-text-muted)' }}>
                  <span className="iw-visually-hidden">current value </span>
                  {f.current}
                </del>
                <span aria-hidden="true">&rarr;</span>
                <ins style={{ color: 'var(--iw-accent)', textDecoration: 'none', fontWeight: 500 }}>
                  <span className="iw-visually-hidden">proposed value </span>
                  {f.proposed}
                </ins>
              </>
            ) : (
              <span>{f.proposed} (unchanged)</span>
            )}
          </div>
        )
      })}
    </div>
  )
}
